import type { Ionicons } from '@expo/vector-icons';

/**
 * Icon + colour per document category (issue #373).
 *
 * Keyed on the category names from `.hermes/desktop-attachments/AGENTS.md` — the ten plus
 * `Letters`, which that doc treats as a first-class structural peer rather than an
 * eleventh category. From this file's point of view they are all just names. But the
 * FALLBACK is the important part: folder names come off the drive at run time, so someone
 * can add a category without touching this file. An unmapped name must render a generic
 * folder rather than crash on a missing Ionicons glyph.
 */

export interface FolderVisual {
	icon: keyof typeof Ionicons.glyphMap;
	color: string;
}

/**
 * The glyph for an association TYPE.
 *
 * Shared with `components/associations/association-icon.tsx` so a community and its ROOT
 * BUCKET cannot drift apart — someone scanning the browser should see the same mark for
 * "condo" in the root column that they see beside the association everywhere else.
 */
export function associationTypeIcon(type?: string | null): keyof typeof Ionicons.glyphMap {
	return (type ?? '').trim().toUpperCase() === 'HOA' ? 'home' : 'business';
}

const FOLDER_VISUALS: Record<string, FolderVisual> = {
	// ── Root buckets. Not categories, but the browser renders every folder row the same way.
	// The two association buckets borrow the association TYPE icons rather than a generic
	// folder, so the root reads as "communities" rather than "some directories".
	'Condos': { icon: associationTypeIcon('condo'), color: '#3949ab' },
	'HOAs': { icon: associationTypeIcon('HOA'), color: '#00897b' },
	'Other': { icon: 'ellipsis-horizontal-circle-outline', color: '#78909c' },
	// Shared libraries — company-wide filing, not a community.
	'General': { icon: 'library-outline', color: '#5e35b1' },
	'Inbox (Scanned Documents)': { icon: 'scan-outline', color: '#00838f' },
	// A top-level bucket rather than a category, but it shares this map because the browser
	// renders every folder row the same way. Deliberately the most muted entry here: it sits
	// beside Condos and HOAs in the root column and should read as the quiet one.
	'Archived': { icon: 'archive-outline', color: '#90a4ae' },
	'Certificates': { icon: 'ribbon-outline', color: '#00897b' },
	'Contracts': { icon: 'document-text-outline', color: '#3949ab' },
	'Documents': { icon: 'documents-outline', color: '#546e7a' },
	'Financial-Banking': { icon: 'cash-outline', color: '#2e7d32' },
	'Insurance': { icon: 'shield-checkmark-outline', color: '#1976d2' },
	'Legal-Docs': { icon: 'briefcase-outline', color: '#6d4c41' },
	'Letters': { icon: 'mail-outline', color: '#c2185b' },
	'Maintenance': { icon: 'construct-outline', color: '#ef6c00' },
	'Meetings': { icon: 'people-outline', color: '#8e24aa' },
	'Owner-Files': { icon: 'folder-open-outline', color: '#0097a7' },
	'Prior-Management': { icon: 'archive-outline', color: '#757575' },
	// Inspection reports and photos, filed by date. Lives under `General` today (company-wide
	// rather than per-community) and is listed here because the browser renders every folder
	// row through this map, whatever level it sits at.
	'Inspections': { icon: 'camera-outline', color: '#0288d1' },
	'BUDGETS': { icon: 'calculator-outline', color: '#2e7d32' },
};

/**
 * Keyed on the FOLDED name.
 *
 * The map above is written in the taxonomy's canonical casing, but the names arriving here
 * come off the drive, where a great many folders are ALL CAPS by hand — `MAINTENANCE` was
 * getting a plain folder icon while `Maintenance` got the wrench. Same defect as the
 * association matching this file's siblings were just fixed for, one layer up: a byte-wise
 * lookup against a case-preserving store.
 */
const VISUAL_BY_KEY = new Map<string, FolderVisual>(
	Object.entries(FOLDER_VISUALS).map(([name, visual]) => [name.toLowerCase(), visual]),
);

const DEFAULT_VISUAL: FolderVisual = { icon: 'folder-outline', color: '#78909c' };

/** Year folders (2025/, 2026/) sit under five of the categories — give them their own look. */
const YEAR_VISUAL: FolderVisual = { icon: 'calendar-outline', color: '#5c6bc0' };

/**
 * A DATE folder: `2026`, `2026-04`, or `2026-04-28`.
 *
 * The year-only form is what the association template seeds. The month and day forms are what
 * date-filed trees actually accumulate — `General/Inspections/2026-04` holds a month's
 * reports and photos — and without them a whole tree of dates renders as anonymous folders.
 */
const DATE_FOLDER = /^\d{4}(-\d{2}){0,2}$/;

export function folderVisual(name: string): FolderVisual {
	const known = VISUAL_BY_KEY.get(name.toLowerCase());
	if (known) return known;
	if (DATE_FOLDER.test(name)) return YEAR_VISUAL;
	return DEFAULT_VISUAL;
}

/** Icon for a file, by extension. Unknown types get a plain document. */
export function fileVisual(extension: string): FolderVisual {
	switch (extension.toLowerCase()) {
		case 'pdf': return { icon: 'document-text', color: '#c62828' };
		case 'doc': case 'docx': return { icon: 'document', color: '#1565c0' };
		case 'xls': case 'xlsx': case 'csv': return { icon: 'grid', color: '#2e7d32' };
		case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': case 'svg':
			return { icon: 'image', color: '#6a1b9a' };
		case 'zip': return { icon: 'file-tray-full', color: '#6d4c41' };
		case 'txt': return { icon: 'reader', color: '#546e7a' };
		default: return { icon: 'document-outline', color: '#78909c' };
	}
}
